import { GroupSecurity } from '@quromedical/fhir-common';
import type {
  AllergyCreateRequest,
  AllergyGetAllResponse,
} from '@quromedical/models';
import {
  AllergyListForm,
  type AllergyTable,
  createAllergyColumns,
} from 'core/allergy';
import { WithGroup } from 'core/auth';
import { PaginatedTable } from 'core/table';
import { Card, Section, Skeleton, SkeletonProvider } from 'design-system';
import { logger } from 'helpers';
import { usePatientAllergy } from 'hooks/useAllergies';
import type { FetcherOptions } from 'hooks/usePaginatedData';
import type React from 'react';
import { useCallback } from 'react';
import { strings } from 'strings';

interface PatientAllergyTabProps {
  patientId: string;
}

export const PatientAllergyTab: React.FC<PatientAllergyTabProps> = ({
  patientId,
}) => {
  const {
    nextToken,
    setPageToken,
    setCount,
    items,
    isLoading,
    cacheKey,
    update,
    create,
  } = usePatientAllergy({
    patientId,
  });

  const fetcher = useCallback(
    async (options?: FetcherOptions) => {
      setPageToken(options?.nextToken);
      setCount(options?.count);
      return { items, nextToken };
    },
    [setPageToken, setCount, items, nextToken],
  );

  const getDataFromResponse = (
    allergyResponse: AllergyGetAllResponse,
  ): AllergyTable[] =>
    allergyResponse.items.map((allergy) => {
      return {
        id: allergy.id,
        name: allergy.name,
        recorder: allergy.recorder.display,
        type: allergy.type,
        severity: allergy.severity,
      };
    });

  const columns = createAllergyColumns({ update, isLoading });

  const handleSubmit = useCallback(
    async (data: AllergyCreateRequest) => {
      try {
        const { items, patient, admission } = data;

        await create({ items, patient, admission });

        return {};
      } catch (error) {
        logger.error(error);
        return { error };
      }
    },
    [create],
  );

  return (
    <>
      <WithGroup groups={[GroupSecurity.AllergyIntoleranceCreate]}>
        <SkeletonProvider loading={isLoading}>
          <Section unsetZIndex>
            <Skeleton>
              <Card unsetZIndex>
                <AllergyListForm
                  handleSubmit={handleSubmit}
                  patient={patientId}
                  title={strings.AllergyCreateTitle}
                  showSubmitButton={true}
                />
              </Card>
            </Skeleton>
          </Section>
        </SkeletonProvider>
      </WithGroup>
      <WithGroup groups={[GroupSecurity.AllergyIntoleranceRead]}>
        <SkeletonProvider loading={isLoading}>
          <Section unsetZIndex>
            <Skeleton>
              <PaginatedTable
                cacheKey={cacheKey}
                fetcher={fetcher}
                columns={columns}
                showHeader={true}
                getDataFromResponse={getDataFromResponse}
              />
            </Skeleton>
          </Section>
        </SkeletonProvider>
      </WithGroup>
    </>
  );
};
